import React from 'react';
import PropTypes from 'prop-types';

import isSameDay from 'date-fns/isSameDay';
import setYear from 'date-fns/setYear';
import setMonth from 'date-fns/setMonth';
import setDate from 'date-fns/setDate';

import Popover from '../Popover';
import Calendar from '../Calendar';
import DateInput from './DateInput';

import { PopoverCommonProps } from '../common/PropTypes/PopoverCommon';

import { st, classes } from './DatePicker.st.css';
import { dataHooks, MAX_DATE } from './constants';

import { WixStyleReactEnvironmentContext } from '../WixStyleReactEnvironmentProvider/context';

import { SupportedWixLocales } from 'wix-design-systems-locale-utils';

/**
 * DatePicker component
 */

export default class DatePicker extends React.PureComponent {
  static displayName = 'DatePicker';

  static defaultProps = {
    filterDate: () => true,
    rtl: false,
    width: '150px',
    zIndex: 1,
    disabled: false,
    inputDataHook: dataHooks.datePickerInput,
    popoverProps: {
      zIndex: 1,
    },
    disableKeyboardType: false,
    onChange: () => {},
    dateStyle: 'short',
    shouldCloseOnSelect: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      value: props.value || new Date(),
      isOpen: props.initialOpen && !props.disabled,
      inputValue: props.value,
      autoFocus: false,
      shouldOpenCalendar: true,
    };
  }

  componentDidUpdate = prevProps => {
    const { value: prevValue } = prevProps;
    const { value: newValue } = this.props;
    if (newValue !== prevValue) {
      if (newValue) {
        if (newValue !== this.state.value) {
          this.setState({ inputValue: newValue });
        }
        this._saveNewValue(newValue);
        return;
      }

      const value = this._transformDate(new Date(), prevValue);
      return this.setState({ inputValue: null, value });
    }
  };

  _openCalendar = () => {
    if (
      !this.state.isOpen &&
      !this.props.readOnly &&
      this.state.shouldOpenCalendar
    ) {
      return this.setState(
        {
          isOpen: true,
          value: this.props.value || new Date(),
        },
        () => {
          this.props.onOpen?.();
        },
      );
    }

    if (!this.state.shouldOpenCalendar) {
      return this.setState({
        shouldOpenCalendar: true,
      });
    }
  };

  openCalendar = this._openCalendar;

  _closeCalendar = (shouldOpenCalendar = true, shouldFocusInput = false) => {
    this.setState(
      { isOpen: false, autoFocus: false, shouldOpenCalendar },
      () => {
        this.props.onClose?.();
        if (shouldFocusInput) {
          this.state.inputRef?.focus();
        }
      },
    );
  };

  _handleInputChange = ({ dateVal }) => {
    this._saveNewValue(dateVal);
  };

  _transformDate = (value, oldValue) =>
    [
      [value.getFullYear(), setYear],
      [value.getMonth(), setMonth],
      [value.getDate(), setDate],
    ].reduce(
      (_value, [datePart, setter]) => setter(_value, datePart),
      oldValue,
    );

  _saveNewValue = (value, modifiers = {}) => {
    if (modifiers.disabled) {
      return;
    }

    const oldValue =
      this.props.value || new Date(new Date().setHours(0, 0, 0, 0));
    const newValue = this._transformDate(value, oldValue);

    if (!isSameDay(value, this.props.value)) {
      this.setState({ value: newValue }, () => this.props.onChange(newValue));
    }
  };

  _filterDate = date => date < MAX_DATE && this.props.filterDate(date);

  _handleKeyDown = (event, newDate) => {
    if (!this.state.isOpen) {
      this._openCalendar();
    }
    switch (event.key) {
      case 'Tab':
      case 'Escape': {
        this._closeCalendar();
        return;
      }
      case 'Enter': {
        event.preventDefault();
        if (newDate) {
          const transformedDate = this._transformDate(
            newDate,
            new Date(new Date().setHours(0, 0, 0, 0)),
          );
          this.setState(
            {
              value: transformedDate,
              inputValue: transformedDate,
            },
            () => this.props.onChange(transformedDate),
          );
        }
        this._closeCalendar();
        return;
      }
      case 'ArrowUp':
      case 'ArrowDown': {
        event.preventDefault();
        this.setState({ autoFocus: true });
        return;
      }
      default:
        return;
    }
  };

  _handleCalendarKeyDown = event => {
    switch (event.key) {
      case 'Escape': {
        event.stopPropagation();
        this._closeCalendar(false, true);
        return;
      }
      default:
        return;
    }
  };

  _handleChange = (value, modifiers) => {
    this.setState({
      inputValue: value,
      isOpen: !this.props.shouldCloseOnSelect || false,
      shouldOpenCalendar: !this.props.shouldCloseOnSelect || false,
    });
    this._saveNewValue(value, modifiers);
  };

  _handleRef = ref => {
    this.setState({ inputRef: ref });
  };

  _handleInputFocus = event => {
    if (!event.isTrusted) {
      return;
    }

    this._openCalendar();

    this.setState({ autoFocus: false }, () => {
      this.state.inputRef?.focus();
      this.props.inputProps?.onFocus?.(event);
    });
  };

  _getLocale() {
    return this.props.locale || this.context.locale || 'en';
  }

  _renderInput = () => {
    const {
      inputDataHook,
      disabled,
      placeholderText,
      readOnly,
      status,
      statusMessage,
      customInput,
      inputProps = {},
      size,
      clearButton,
      onClear,
      disableKeyboardType,
      dateStyle,
      validate,
      onValidate,
      excludePastDates,
      clearButtonTooltipContent,
      clearButtonTooltipProps,
    } = this.props;
    const { onFocus, ...inputPropsRest } = inputProps;
    return (
      <DateInput
        dataHook={inputDataHook}
        className={classes.input}
        value={this.state.inputValue}
        onInputClicked={this._openCalendar}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholderText}
        onFocus={this._handleInputFocus}
        onKeyDown={this._handleKeyDown}
        tabIndex={0}
        status={status}
        statusMessage={statusMessage}
        autoSelect={false}
        customInput={customInput}
        locale={this._getLocale()}
        dateStyle={dateStyle}
        size={size}
        clearButton={clearButton}
        onClear={onClear}
        onChange={this._handleInputChange}
        disableEditing={disableKeyboardType}
        validate={validate}
        onValidate={onValidate}
        excludePastDates={excludePastDates}
        clearButtonTooltipContent={clearButtonTooltipContent}
        clearButtonTooltipProps={clearButtonTooltipProps}
        inputRef={this._handleRef}
        focusOnClearClick
        {...(customInput ? customInput.props : {})}
        {...inputPropsRest}
      />
    );
  };

  render() {
    const {
      className,
      showMonthDropdown,
      showYearDropdown,
      excludePastDates,
      rtl,
      shouldCloseOnSelect,
      width,
      calendarDataHook,
      twoMonths,
      zIndex,
      dataHook,
      popoverProps,
      firstDayOfWeek,
      leftArrowAriaLabel,
      leftArrowAriaLabelledBy,
      rightArrowAriaLabel,
      rightArrowAriaLabelledBy,
      monthDropdownAriaLabel,
      monthDropdownAriaLabelledBy,
      yearDropdownAriaLabel,
      yearDropdownAriaLabelledBy,
      disableKeyboardType,
      dateIndication,
      disabled,
    } = this.props;

    const { isOpen, value } = this.state;

    const popoverUpdatedProps = {
      placement: rtl ? 'top-end' : 'top-start',
      ...popoverProps,
    };

    const calendarProps = {
      dataHook: dataHooks.datePickerCalendar,
      className: classes.calendar,
      locale: this._getLocale(),
      showMonthDropdown,
      showYearDropdown,
      filterDate: this._filterDate,
      excludePastDates,
      rtl,
      onChange: this._handleChange,
      onClose: () => this._closeCalendar(false),
      value,
      shouldCloseOnSelect,
      numOfMonths: twoMonths ? 2 : 1,
      firstDayOfWeek,
      leftArrowAriaLabel,
      leftArrowAriaLabelledBy,
      rightArrowAriaLabel,
      rightArrowAriaLabelledBy,
      monthDropdownAriaLabel,
      monthDropdownAriaLabelledBy,
      yearDropdownAriaLabel,
      yearDropdownAriaLabelledBy,
      dateIndication,
      autoFocus: this.state.autoFocus || false,
      containFocus: true,
      onKeyDown: this._handleCalendarKeyDown,
    };

    return (
      <div
        className={st(
          classes.root,
          { disableKeyboardType, disabled },
          className,
        )}
        data-hook={dataHook}
        style={{ width }}
      >
        <Popover
          className={classes.popover}
          dataHook={dataHooks.datePickerPopover}
          onClickOutside={() => this._closeCalendar(false)}
          appendTo="parent"
          shown={isOpen}
          zIndex={zIndex}
          tabIndex={-1}
          {...popoverUpdatedProps}
        >
          <Popover.Element>
            <div
              className={classes.inputContainer}
              data-hook={dataHooks.datePickerInputContainer}
            >
              {this._renderInput()}
            </div>
          </Popover.Element>
          <Popover.Content>
            <div data-hook={calendarDataHook}>
              <Calendar {...calendarProps} />
            </div>
          </Popover.Content>
        </Popover>
      </div>
    );
  }
}

DatePicker.contextType = WixStyleReactEnvironmentContext;

DatePicker.propTypes = {
  /** Specify a single CSS class name to be appended to the root element */
  className: PropTypes.string,

  /** Applies as data-hook HTML attribute that can be used in the tests */
  dataHook: PropTypes.string,

  /** Focus selected day automatically when component mounts or updates */
  autoFocus: PropTypes.bool,

  /** Override a field with a custom input element. If you only need to pass custom props to the `<Input/>`, then use `inputProps` instead. */
  customInput: PropTypes.node,

  /** Allows you to pass default Input component properties */
  inputProps: PropTypes.object,

  /**
   ##### Add an indication under a specific date.
   Function returns the indication node of a specific date or null if this day doesn't have an indication.
   * - `param` {date: Date, isSelected: boolean }
   * - `date` - a date
   * - `isSelected` - whether this date is the selected date
   * - `return` {React.node} - the indication node of a specific date or null if this day doesn't have an indication.
  */
  dateIndication: PropTypes.func,

  /** Specify date picker instance locale */
  locale: PropTypes.oneOf(SupportedWixLocales),

  /** Sets date format of locale */
  dateStyle: PropTypes.oneOf(['short', 'medium']),

  /** Specify whether a field should be disabled or not */
  disabled: PropTypes.bool,

  /** Specify whether past dates should be selectable or not */
  excludePastDates: PropTypes.bool,

  /**
   * ##### Specify selectable dates:
   *  * `param` {Date} `date` - a date to check
   *  * `return` {boolean} - true if `date` should be selectable, false otherwise
   */
  filterDate: PropTypes.func,

  /** Applies a data-hook HTML attribute for date picker input */
  inputDataHook: PropTypes.string,

  /** Applies a data-hook HTML attribute for date picker calendar view */
  calendarDataHook: PropTypes.string,

  /** Defines a placeholder of the field */
  placeholderText: PropTypes.string,

  /** Specify whether RTL mode is enabled or not. When true, the keyboard navigation will be changed, meaning pressing on the right arrow will navigate to the previous day, and pressing on the left arrow will navigate to the next day. */
  rtl: PropTypes.bool,

  /** Defines the selected date */
  value: PropTypes.object,

  /** Specify whether the calendar will be initially visible or not */
  initialOpen: PropTypes.bool,

  /** Controls the status of a field */
  status: PropTypes.oneOf(['error', 'warning', 'loading']),

  /** Defines the status message to be displayed on status icon hover. If not given or empty, the tooltip won’t be shown. */
  statusMessage: PropTypes.node,

  /** Sets the width of picker input in pixels or percentage */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** Set a desired z-index for date picker popover */
  zIndex: PropTypes.number,

  /** Allows you to pass popover properties. The default placement value depends on the rtl prop - would be 'top-start' when rtl=false and 'top-end' in case of rtl=ture. */
  popoverProps: PropTypes.shape(PopoverCommonProps),

  /** Specify the starting day of a week, allowing only from 0 to 6 (Sunday to Saturday). The default value is 1 which means Monday. */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),

  /** Specifies the size of the input */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Specify whether date picker input is readOnly or not */
  readOnly: PropTypes.bool,

  /** Sets today's date. The today indication is added automatically according to the user timezone but in some cases, we need the ability to add the today indication based on the business timezone. */
  today: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),

  /** Display a clear button (x) on a non-empty field */
  clearButton: PropTypes.bool,

  /** Provides a callback function when  day in selected in the calendar */
  onChange: PropTypes.func.isRequired,

  /** Displays clear button (X) on a non-empty input and calls callback with no arguments */
  onClear: PropTypes.func,

  /** Defines a callback function that is  called whenever a user presses escape, clicks outside of the element or a date is selected and `shouldCloseOnSelect` is set. Receives an event as a first argument. */
  onClose: PropTypes.func,

  /** Defines a callback function which is called when date picker is opened. */
  onOpen: PropTypes.func,

  /** Defines a callback function that is called with the date of the first day of the month whenever the user selects a month in the calendar */
  onMonthChange: PropTypes.func,

  /** Disable typing the in the input. When true, choosing a date is possible only by picking from the calendar. Default: false.  */
  disableKeyboardType: PropTypes.bool,

  /** Displays a selectable yearDropdown */
  showYearDropdown: PropTypes.bool,

  /** Displays a selectable monthDropdown */
  showMonthDropdown: PropTypes.bool,

  /** Specify whether the calendar closes on day selection */
  shouldCloseOnSelect: PropTypes.bool,

  /** Defines a string value that labels the months dropdown in calendar header */
  monthDropdownAriaLabel: PropTypes.string,

  /** Identifies the element that labels the months dropdown in calendar header */
  monthDropdownAriaLabelledBy: PropTypes.string,

  /** Defines a string value that labels the years dropdown in calendar header */
  yearDropdownAriaLabel: PropTypes.string,

  /** Identifies the element that labels the years dropdown in calendar header */
  yearDropdownAriaLabelledBy: PropTypes.string,

  /** Defines a string value that labels the left arrow in calendar header */
  leftArrowAriaLabel: PropTypes.string,

  /** Identifies the element that labels the left arrow in calendar header */
  leftArrowAriaLabelledBy: PropTypes.string,

  /** Defines a string value that labels the right arrow in calendar header */
  rightArrowAriaLabel: PropTypes.string,

  /** Identifies the element that labels the right arrow in calendar header */
  rightArrowAriaLabelledBy: PropTypes.string,

  /** Enables internal input validation */
  validate: PropTypes.bool,

  /** Defines a callback function which is called on cases when date is validated.
   * - {validationType: 'outOfBoundsError' | 'formatError' | 'valid', format: string, value: string }
   * - `validationType` - type 'formatError' is set when validated date is in the wrong date format
   *                    - type 'outOfBoundsError' is set when 'excludePastDates' is true and date input is before today
   *                    - type 'valid' is set when entered date is valid
   * - `format` - is set to valid date format
   * - `value` - is set to current input value
   */
  onValidate: PropTypes.func,

  /** When provided, hover will display a tooltip */
  clearButtonTooltipContent: PropTypes.node,

  /** Tooltip props */
  clearButtonTooltipProps: PropTypes.node,
};
